import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
import { Survey, SurveyPayload, SurveyResponse, SurveyFilters, SurveyType, SurveyCategory } from './types';
import { fetchSurveys, createSurvey, updateSurvey, deleteSurvey, getSurveyById } from './surveysApi';

interface SurveysState {
  items: Survey[];
  currentSurvey: Survey | null;
  loading: boolean;
  error: string | null;
  pagination: {
    page: number;
    pageSize: number;
    total: number;
    pages: number;
  };
  filters: {
    search: string;
    category?: SurveyCategory;
    type?: SurveyType;
    active?: boolean;
    sortBy: 'title' | 'createdAt' | 'updatedAt';
    sortDir: 'asc' | 'desc';
  };
}

const initialState: SurveysState = {
  items: [],
  currentSurvey: null,
  loading: false,
  error: null,
  pagination: { page: 1, pageSize: 20, total: 0, pages: 0 },
  filters: {
    search: '',
    active: undefined,
    sortBy: 'createdAt',
    sortDir: 'desc',
  },
};

export const loadSurveys = createAsyncThunk<SurveyResponse, SurveyFilters | undefined>(
  'surveys/load',
  async (params) => fetchSurveys(params)
);

export const loadSurveyById = createAsyncThunk<Survey, string>(
  'surveys/loadById',
  async (id) => getSurveyById(id)
);

export const addSurvey = createAsyncThunk<Survey, SurveyPayload>(
  'surveys/add',
  async (payload) => createSurvey(payload)
);

export const editSurvey = createAsyncThunk<Survey, { id: string; payload: Partial<SurveyPayload & { active?: boolean }> }>(
  'surveys/edit',
  async ({ id, payload }) => updateSurvey(id, payload)
);

export const removeSurvey = createAsyncThunk<string, string>(
  'surveys/remove',
  async (id) => {
    await deleteSurvey(id);
    return id;
  }
);

const surveysSlice = createSlice({
  name: 'surveys',
  initialState,
  reducers: {
    setSurveysSearch(state, action: PayloadAction<string>) {
      state.filters.search = action.payload;
    },
    setSurveysPage(state, action: PayloadAction<number>) {
      state.pagination.page = action.payload;
    },
    setSurveysPageSize(state, action: PayloadAction<number>) {
      state.pagination.pageSize = action.payload;
    },
    setSurveysCategory(state, action: PayloadAction<SurveyCategory | undefined>) {
      state.filters.category = action.payload;
    },
    setSurveysType(state, action: PayloadAction<SurveyType | undefined>) {
      state.filters.type = action.payload;
    },
    setSurveysActive(state, action: PayloadAction<boolean | undefined>) {
      state.filters.active = action.payload;
    },
    setSurveysSort(state, action: PayloadAction<{ sortBy: 'title' | 'createdAt' | 'updatedAt'; sortDir: 'asc' | 'desc' }>) {
      state.filters.sortBy = action.payload.sortBy;
      state.filters.sortDir = action.payload.sortDir;
    },
    clearCurrentSurvey(state) {
      state.currentSurvey = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(loadSurveys.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(loadSurveys.fulfilled, (state, action: PayloadAction<SurveyResponse>) => {
        state.loading = false;
        state.items = action.payload.data;
        state.pagination = action.payload.pagination;
      })
      .addCase(loadSurveys.rejected, (state) => {
        state.loading = false;
        state.error = 'No se pudieron cargar las encuestas';
      })
      .addCase(loadSurveyById.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(loadSurveyById.fulfilled, (state, action: PayloadAction<Survey>) => {
        state.loading = false;
        state.currentSurvey = action.payload;
      })
      .addCase(loadSurveyById.rejected, (state) => {
        state.loading = false;
        state.error = 'No se pudo cargar la encuesta';
      })
      .addCase(addSurvey.fulfilled, (state, action) => {
        state.items = [action.payload, ...state.items];
      })
      .addCase(editSurvey.fulfilled, (state, action) => {
        state.items = state.items.map((s) => (s.id === action.payload.id ? action.payload : s));
        if (state.currentSurvey?.id === action.payload.id) {
          state.currentSurvey = action.payload;
        }
      })
      .addCase(removeSurvey.fulfilled, (state, action) => {
        state.items = state.items.filter((s) => s.id !== action.payload);
        if (state.currentSurvey?.id === action.payload) {
          state.currentSurvey = null;
        }
      });
  },
});

export const {
  setSurveysSearch,
  setSurveysPage,
  setSurveysPageSize,
  setSurveysCategory,
  setSurveysType,
  setSurveysActive,
  setSurveysSort,
  clearCurrentSurvey,
} = surveysSlice.actions;

export default surveysSlice.reducer;

